<template>
  <!-- Recursive dispatcher: layout blocks recurse into <ManifestBlock>;
       primitive blocks render their visual. Unknown types render an
       empty <span> so a malformed manifest never breaks the page. -->

  <!-- ============ Layout ============ -->
  <div v-if="block.type === 'stack'" class="mb-stack" :style="{ gap: gap(block) }">
    <ManifestBlock v-for="(child, i) in block.children" :key="i" :block="child" />
  </div>

  <div
    v-else-if="block.type === 'row'"
    class="mb-row"
    :class="rowClass"
    :style="{ gap: gap(block) }"
  >
    <ManifestBlock v-for="(child, i) in block.children" :key="i" :block="child" />
  </div>

  <div v-else-if="block.type === 'grid'" class="mb-grid" :style="gridStyle">
    <ManifestBlock v-for="(child, i) in block.children" :key="i" :block="child" />
  </div>

  <div v-else-if="block.type === 'divider'" class="mb-divider" :class="{ '--dashed': block.style === 'dashed' }"></div>

  <!-- ============ Skeleton ============ -->
  <span v-else-if="block.type === 'bar'" class="mb-bar" :class="['mb-bar--' + block.size, { '--emph': block.emphasis }]"></span>

  <span v-else-if="block.type === 'dot'" class="mb-dot" :class="{ '--accent': block.accent }"></span>

  <span v-else-if="block.type === 'letter'" class="mb-letter" :class="{ '--accent': block.accent }">{{ block.value }}</span>

  <span v-else-if="block.type === 'text'" class="mb-text" :class="['mb-text--' + (block.size || 'sm'), block.weight === 'bold' ? '--bold' : '']">{{ block.value }}</span>

  <!-- ============ Result rows (Pick Answer / Trivia / Odd-One-Out / quiz pattern) ============ -->
  <div v-else-if="block.type === 'result-rows'" class="mb-results">
    <div
      v-for="(opt, i) in block.options"
      :key="i"
      class="mb-result-row"
      :class="{ '--correct': opt.correct }"
    >
      <span class="mb-letter" :class="{ '--accent': opt.correct }">{{ opt.label }}</span>
      <div class="mb-track">
        <div
          class="mb-fill"
          :class="{ '--correct': opt.correct }"
          :style="{ '--row-pct': opt.pct + '%', '--row-i': i }"
        ></div>
      </div>
      <span v-if="opt.count != null" class="mb-count">{{ opt.count }}</span>
      <span v-if="opt.correct" class="material-symbols-rounded mb-check">check</span>
    </div>
  </div>

  <!-- ============ Bar chart (Poll / Sentiment / Mood / Two-Truths) ============ -->
  <div
    v-else-if="block.type === 'bar-chart'"
    class="mb-chart"
    :class="block.orientation === 'v' ? '--vertical' : '--horizontal'"
  >
    <div
      v-for="(b, i) in block.bars"
      :key="i"
      class="mb-chart-row"
    >
      <span v-if="b.label && block.orientation !== 'v'" class="mb-chart-label">{{ b.label }}</span>
      <div class="mb-chart-track">
        <div class="mb-chart-fill" :style="{ '--row-pct': b.pct + '%', '--row-i': i }"></div>
      </div>
      <span v-if="block.orientation === 'v' && b.label" class="mb-chart-vlabel">{{ b.label }}</span>
    </div>
  </div>

  <!-- ============ Audience inputs ============ -->
  <div v-else-if="block.type === 'option-buttons'" class="mb-options">
    <button
      v-for="(label, i) in block.labels"
      :key="i"
      type="button"
      class="mb-option"
      :class="{ '--tapped': i === block.tapIndex }"
      tabindex="-1"
    >
      <span class="mb-letter" :class="{ '--accent': i === block.tapIndex }">{{ label }}</span>
      <span class="mb-bar mb-bar--md"></span>
    </button>
  </div>

  <div v-else-if="block.type === 'text-input'" class="mb-input">
    <span v-if="block.typing" class="mb-input-typed">{{ block.typing }}</span>
    <span v-else class="mb-input-placeholder">{{ block.placeholder || '' }}</span>
  </div>

  <button
    v-else-if="block.type === 'send-button'"
    type="button"
    class="mb-send"
    tabindex="-1"
  >
    <span class="material-symbols-rounded">send</span>
    <span v-if="block.label">{{ block.label }}</span>
  </button>

  <!-- ============ Word cloud ============ -->
  <div v-else-if="block.type === 'word-cloud'" class="mb-cloud">
    <span
      v-for="(w, i) in block.words"
      :key="i"
      class="mb-cloud-word"
      :style="{ fontSize: (10 + w.size * 1.4) + 'px', '--word-i': i }"
    >{{ w.text }}</span>
    <span v-if="block.incomingWord" class="mb-cloud-incoming">{{ block.incomingWord }}</span>
  </div>

  <!-- ============ Pin on image ============ -->
  <div v-else-if="block.type === 'pin-image'" class="mb-pin-image">
    <span
      v-for="(p, i) in block.pins"
      :key="i"
      class="mb-pin"
      :style="{ left: p.x + '%', top: p.y + '%', '--intensity': p.intensity || 0.6 }"
    ></span>
  </div>

  <!-- ============ Sticky notes (idea board / brainstorm / SWOT) ============ -->
  <div v-else-if="block.type === 'sticky-notes'" class="mb-board">
    <span
      v-for="(n, i) in block.notes"
      :key="i"
      class="mb-note"
      :style="{
        left: n.x + '%', top: n.y + '%',
        width: (n.w || 22) + '%', height: (n.h || 22) + '%',
        background: n.color || stickyColor(i),
        '--note-i': i,
      }"
    ></span>
  </div>

  <!-- ============ Wheel (spinner) ============ -->
  <div
    v-else-if="block.type === 'wheel'"
    class="mb-wheel"
    :style="{ '--seg-count': block.segments.length, '--stop': block.stopAt }"
  >
    <span
      v-for="(seg, i) in block.segments"
      :key="i"
      class="mb-wheel-seg"
      :style="{
        '--seg-i': i,
        '--seg-color': seg.color || (i % 2 ? 'var(--accent)' : '#FFFFFF22'),
      }"
    ></span>
    <span class="mb-wheel-pin"></span>
  </div>

  <!-- ============ Image / video / QR / embed mocks ============ -->
  <div v-else-if="block.type === 'image-frame'" class="mb-image" :class="'--' + (block.aspect || 'wide')"></div>

  <div v-else-if="block.type === 'video-frame'" class="mb-video">
    <span class="material-symbols-rounded mb-video-play">play_arrow</span>
    <span class="mb-video-bar"></span>
  </div>

  <div v-else-if="block.type === 'qr-grid'" class="mb-qr" :style="{ '--qr-size': block.size || 9 }">
    <span
      v-for="i in qrCells(block.size || 9)"
      :key="i"
      class="mb-qr-cell"
      :class="{ '--on': qrOn(i, block.size || 9) }"
    ></span>
  </div>

  <!-- ============ Fallback for unimplemented primitives ============ -->
  <span v-else class="mb-todo" :title="block.type">[{{ block.type }}]</span>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { Block } from '../../../lib/manifestTypes'

const STICKY_COLORS = ['#FFE8B5', '#D5F0FF', '#FFD3DD', '#D9F3D6', '#E5D5FF']

export default Vue.extend({
  name: 'ManifestBlock',
  props: {
    block: { type: Object as PropType<Block>, required: true },
  },
  computed: {
    rowClass(): string[] {
      const { align } = this.block as any
      return align ? [`--align-${align}`] : []
    },
    gridStyle(): Record<string, string> {
      const b = this.block as any
      return {
        gap: this.gap(b),
        gridTemplateColumns: `repeat(${b.cols || 1}, 1fr)`,
      }
    },
  },
  methods: {
    gap(b: any): string {
      return `${b.gap ?? 8}px`
    },
    stickyColor(i: number): string {
      return STICKY_COLORS[i % STICKY_COLORS.length]
    },
    qrCells(size: number): number[] {
      return Array.from({ length: size * size }, (_, i) => i)
    },
    qrOn(i: number, size: number): boolean {
      // Deterministic pattern (cheap PRNG by index) — looks "QR-ish".
      const x = i % size
      const y = Math.floor(i / size)
      // Always paint corner-eye finder squares.
      const inEye = (cx: number, cy: number) => x >= cx && x < cx + 3 && y >= cy && y < cy + 3
      if (inEye(0, 0) || inEye(size - 3, 0) || inEye(0, size - 3)) return true
      return ((i * 1103515245 + 12345) >>> 8) % 3 === 0
    },
  },
})
</script>

<style scoped lang="scss">
/* ---------- Layout ---------- */
.mb-stack { display: flex; flex-direction: column; }
.mb-row   { display: flex; flex-direction: row; align-items: center; }
.mb-row.--align-center  { justify-content: center; }
.mb-row.--align-end     { justify-content: flex-end; }
.mb-row.--align-between { justify-content: space-between; }
.mb-grid  { display: grid; }
.mb-divider {
  height: 1px;
  background: rgba(15, 15, 20, 0.08);
  margin: 6px 0;
}
.mb-divider.--dashed {
  background: transparent;
  border-top: 1px dashed color-mix(in srgb, var(--accent) 22%, transparent);
}

/* ---------- Skeleton ---------- */
.mb-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.mb-bar.--emph    { background: rgba(15, 15, 20, 0.22); }
.mb-bar.mb-bar--xl { width: 70%; }
.mb-bar.mb-bar--lg { width: 55%; }
.mb-bar.mb-bar--md { width: 40%; }
.mb-bar.mb-bar--sm { width: 26%; }
.mb-dot {
  display: inline-block; width: 8px; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
}
.mb-dot.--accent { background: var(--accent); }
.mb-letter {
  width: 18px; height: 18px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  color: rgba(15, 15, 20, 0.55);
  font-size: 10px; font-weight: 700;
  display: grid; place-items: center;
  flex-shrink: 0;
}
.mb-letter.--accent { background: var(--accent); color: white; }
.mb-text { font-size: 11px; color: rgba(15, 15, 20, 0.7); }
.mb-text.mb-text--xs { font-size: 9px; }
.mb-text.mb-text--md { font-size: 13px; }
.mb-text.--bold { font-weight: 700; }

/* ---------- Result rows ---------- */
.mb-results { display: grid; gap: 7px; }
.mb-result-row {
  display: grid;
  grid-template-columns: 18px 1fr auto auto;
  align-items: center;
  gap: 8px;
}
.mb-track {
  height: 10px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  overflow: hidden;
}
.mb-fill {
  height: 100%; width: 0;
  border-radius: 999px;
  background: var(--accent);
  /* Cycle synced to the loop CSS variable. duration: 0s effectively
     freezes the animation in its final frame for static manifests. */
  animation:
    mb-grow var(--loop-duration, 4s) cubic-bezier(0.23, 1, 0.32, 1) infinite;
  animation-delay: calc(var(--row-i, 0) * 80ms);
}
.mb-fill.--correct { animation-name: mb-grow-correct; }
.mb-count {
  font-size: 10px; font-weight: 700;
  color: rgba(15, 15, 20, 0.55);
  font-variant-numeric: tabular-nums;
  min-width: 22px; text-align: right;
}
.mb-check { font-size: 14px; color: var(--accent); }

/* ---------- Bar chart ---------- */
.mb-chart.--horizontal { display: grid; gap: 6px; }
.mb-chart.--horizontal .mb-chart-row {
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 8px;
  align-items: center;
}
.mb-chart-label { font-size: 10px; color: rgba(15, 15, 20, 0.6); }
.mb-chart-track {
  height: 10px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  overflow: hidden;
}
.mb-chart-fill {
  height: 100%; width: 0;
  border-radius: 999px;
  background: var(--accent);
  animation: mb-grow var(--loop-duration, 4s) cubic-bezier(0.23, 1, 0.32, 1) infinite;
  animation-delay: calc(var(--row-i, 0) * 100ms);
}
.mb-chart.--vertical {
  display: flex; align-items: flex-end; justify-content: space-around;
  height: 110px; padding: 0 8px;
}
.mb-chart.--vertical .mb-chart-row { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.mb-chart.--vertical .mb-chart-track {
  width: 18px; height: 80px;
  display: flex; align-items: flex-end;
}
.mb-chart.--vertical .mb-chart-fill {
  width: 100%; height: 0;
  animation: mb-grow-v var(--loop-duration, 4s) cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.mb-chart-vlabel { font-size: 10px; color: rgba(15, 15, 20, 0.6); }

/* ---------- Audience inputs ---------- */
.mb-options { display: grid; gap: 7px; }
.mb-option {
  appearance: none; background: white;
  display: grid;
  grid-template-columns: 18px 1fr;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 8px;
  border: 1px solid rgba(15, 15, 20, 0.08);
  cursor: pointer;
}
.mb-option.--tapped {
  border-color: color-mix(in srgb, var(--accent) 60%, transparent);
  background: color-mix(in srgb, var(--accent) 10%, white);
  animation: mb-tap var(--loop-duration, 4s) cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.mb-input {
  background: rgba(15, 15, 20, 0.04);
  border: 1px solid rgba(15, 15, 20, 0.08);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 11px;
  color: rgba(15, 15, 20, 0.7);
  min-height: 30px;
  display: flex; align-items: center;
}
.mb-input-placeholder { color: rgba(15, 15, 20, 0.35); }
.mb-input-typed {
  position: relative;
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
}
.mb-input-typed::after {
  content: ''; display: inline-block;
  width: 1px; height: 12px;
  margin-left: 1px;
  background: var(--accent);
  animation: mb-caret 1s steps(2) infinite;
  vertical-align: middle;
}
.mb-send {
  appearance: none;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 7px 11px;
  border: 0; border-radius: 8px;
  background: var(--accent); color: white;
  font: inherit; font-size: 11px; font-weight: 700;
  cursor: pointer;
  align-self: flex-end;
}
.mb-send .material-symbols-rounded { font-size: 13px; }

/* ---------- Word cloud ---------- */
.mb-cloud {
  position: relative;
  display: flex; flex-wrap: wrap; gap: 6px 10px;
  align-items: center; justify-content: center;
  min-height: 100px;
}
.mb-cloud-word {
  font-weight: 700;
  color: color-mix(in srgb, var(--accent) calc(40% + var(--word-i, 0) * 8%), rgba(15, 15, 20, 0.6));
  line-height: 1;
}
.mb-cloud-incoming {
  position: absolute;
  top: 50%; left: 50%;
  font-weight: 800; color: var(--accent); font-size: 16px;
  animation: mb-cloud-in var(--loop-duration, 4s) cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

/* ---------- Pin on image ---------- */
.mb-pin-image {
  position: relative;
  width: 100%; aspect-ratio: 16 / 9;
  background: color-mix(in srgb, var(--accent) 12%, white);
  border-radius: 8px;
  border: 1px dashed color-mix(in srgb, var(--accent) 30%, transparent);
}
.mb-pin {
  position: absolute;
  width: 12px; height: 12px;
  border-radius: 999px;
  background: var(--accent);
  transform: translate(-50%, -50%);
  opacity: var(--intensity, 0.6);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--accent) calc(20% * var(--intensity, 0.6)), transparent);
}

/* ---------- Sticky notes board ---------- */
.mb-board {
  position: relative;
  width: 100%; aspect-ratio: 16 / 9;
  background: rgba(15, 15, 20, 0.04);
  border-radius: 8px;
  overflow: hidden;
}
.mb-note {
  position: absolute;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.08);
  transform: rotate(calc(var(--note-i, 0) * 7deg - 14deg));
  animation: mb-note-in var(--loop-duration, 4s) cubic-bezier(0.23, 1, 0.32, 1) infinite;
  animation-delay: calc(var(--note-i, 0) * 120ms);
}

/* ---------- Wheel (Spinner) ---------- */
.mb-wheel {
  position: relative;
  width: 100%; aspect-ratio: 1;
  max-width: 140px;
  margin: 0 auto;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(15, 15, 20, 0.04);
  animation: mb-wheel-spin var(--loop-duration, 4s) cubic-bezier(0.32, 0.72, 0, 1) infinite;
  transform-origin: 50% 50%;
}
.mb-wheel-seg {
  position: absolute; inset: 0;
  background: var(--seg-color, var(--accent));
  clip-path: polygon(50% 50%, 50% 0%, calc(50% + 50% * 1) 0%);
  transform: rotate(calc(360deg / var(--seg-count) * var(--seg-i)));
  transform-origin: 50% 50%;
}
.mb-wheel-pin {
  position: absolute; top: -3px; left: 50%;
  width: 0; height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 10px solid #1A1A2E;
  transform: translateX(-50%);
  z-index: 2;
}

/* ---------- Image / video / QR mocks ---------- */
.mb-image {
  background: color-mix(in srgb, var(--accent) 10%, white);
  border: 1px dashed color-mix(in srgb, var(--accent) 30%, transparent);
  border-radius: 8px;
}
.mb-image.--wide { aspect-ratio: 16 / 9; }
.mb-image.--square { aspect-ratio: 1; }
.mb-image.--tall { aspect-ratio: 3 / 4; }

.mb-video {
  position: relative;
  width: 100%; aspect-ratio: 16 / 9;
  background: #1A1A2E;
  border-radius: 8px;
  display: grid; place-items: center;
}
.mb-video-play {
  font-size: 32px; color: white;
  background: rgba(255, 255, 255, 0.18);
  width: 48px; height: 48px; border-radius: 999px;
  display: grid; place-items: center;
}
.mb-video-bar {
  position: absolute; bottom: 8px; left: 12px; right: 12px;
  height: 3px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
}
.mb-video-bar::after {
  content: ''; display: block;
  height: 100%; width: 35%;
  border-radius: 999px;
  background: var(--accent);
}

.mb-qr {
  display: grid;
  grid-template-columns: repeat(var(--qr-size, 9), 1fr);
  gap: 1px;
  width: 100px; height: 100px;
  background: white;
  padding: 4px;
  border: 1px solid rgba(15, 15, 20, 0.1);
  border-radius: 6px;
}
.mb-qr-cell { background: white; }
.mb-qr-cell.--on { background: #1A1A2E; }

.mb-todo {
  display: inline-block;
  padding: 2px 6px;
  font-size: 10px; font-weight: 700;
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 12%, white);
  border-radius: 4px;
}

/* ---------- Animations ---------- */
@keyframes mb-grow {
  0%, 12% { width: 0; }
  60%     { width: var(--row-pct, 50%); }
  92%, 100% { width: var(--row-pct, 50%); }
}
@keyframes mb-grow-correct {
  0%, 12% { width: 0; }
  55%     { width: 78%; }
  92%, 100% { width: 78%; }
}
@keyframes mb-grow-v {
  0%, 12% { height: 0; }
  60%     { height: var(--row-pct, 50%); }
  92%, 100% { height: var(--row-pct, 50%); }
}
@keyframes mb-tap {
  0%, 4%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  18%      { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%     { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}
@keyframes mb-caret {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}
@keyframes mb-cloud-in {
  0%, 60% { opacity: 0; transform: translate(-50%, 12px) scale(0.7); }
  75%     { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  92%, 100% { opacity: 0; transform: translate(-50%, -8px) scale(0.9); }
}
@keyframes mb-note-in {
  0%, 50% { opacity: 0; transform: translateY(8px) rotate(calc(var(--note-i, 0) * 7deg - 14deg)); }
  68%     { opacity: 1; transform: translateY(0) rotate(calc(var(--note-i, 0) * 7deg - 14deg)); }
  100%    { opacity: 1; }
}
@keyframes mb-wheel-spin {
  0%   { transform: rotate(0); }
  60%  { transform: rotate(calc(720deg + (360deg / var(--seg-count, 6)) * var(--stop, 0))); }
  100% { transform: rotate(calc(720deg + (360deg / var(--seg-count, 6)) * var(--stop, 0))); }
}

@media (prefers-reduced-motion: reduce) {
  .mb-fill,
  .mb-chart-fill,
  .mb-option.--tapped,
  .mb-input-typed::after,
  .mb-cloud-incoming,
  .mb-note,
  .mb-wheel { animation: none !important; }
  .mb-fill { width: var(--row-pct, 50%); }
  .mb-fill.--correct { width: 78%; }
}
</style>
